// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { Text, Accent, Box, Title } from '@nerdwallet/currency';
import Disclosure, { disclosureTypes } from '../disclosure/disclosure';
import Image from '../image/image';
import BackgroundColor from '../background-color/background-color';
import classColor from '../../lib/parse-color';

import {
  textColorPicker,
  preheaderInput,
  titleInput,
  boolCheckboxSpec,
  subtitleInput,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';

import './centered-hero.less';

const CenteredHero = ({
  bgOptions,
  backgroundSize,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  textColor,
  subtitle,
  subtitleMobile,
  disclosureType,
  disclosureColor,
  disclosureOnBackground,
  iconImage,
  preheader,
  title,
  size,
  children,
  fontChronicle,
}) => {
  const backgroundImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };
  const imgHolder = {
    backgroundImage: `url(${backgroundImageSrcMobile}),url(${backgroundImageSrcTablet}),url(${backgroundImageSrcDesktop})`,
    display: 'none',
  };

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          className={classNames([
            'centered-hero',
            {
              children,
            },
          ])}
          style={{
            backgroundColor,
            backgroundImage: size && `url(${backgroundImageSrcMap[size]})`,
            backgroundSize: `${backgroundSize}`,
            height: children && 'auto',
          }}
        >
          <div style={imgHolder} />
          {disclosureType && disclosureType !== 'none' && (
            <Box className="centered-hero__advertiser-disclosure">
              <Disclosure
                type={disclosureType}
                color={disclosureColor}
                onBackground={disclosureOnBackground}
              />
            </Box>
          )}
          <Box className={classes('padding-top-5--wide')}>
            {iconImage && iconImage !== '' && (
              <Box
                className={classNames([
                  'centered-hero__icon-wrapper',
                  {
                    spaced: !preheader,
                  },
                ])}
              >
                <Image src={iconImage} alt="" className="centered-hero__icon" />
              </Box>
            )}
            <Box
              className={classNames([
                'centered-hero__content',
                {
                  children,
                },
              ])}
            >
              {preheader && preheader !== '' && (
                <Box
                  className={classNames([
                    'centered-hero__preheader',
                    classColor(textColor),
                    {
                      spaced: !iconImage,
                    },
                  ])}
                >
                  <Accent size="large">{preheader}</Accent>
                </Box>
              )}
              <Title
                className={classNames([
                  'centered-hero__title',
                  classColor(textColor),
                  {
                    gotham: !fontChronicle,
                  },
                ])}
              >
                {title}
              </Title>
              <Box
                className={classNames([
                  'centered-hero__subtitle',
                  classColor(textColor),
                ])}
              >
                <Text size="default">
                  {(size === 'sm' && subtitleMobile) || subtitle}
                </Text>
              </Box>
            </Box>
          </Box>
          {children}
        </div>
      )}
    </BackgroundColor>
  );
};

CenteredHero.propTypes = {
  iconImage: PropTypes.string,
  preheader: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  ...BackgroundColor.propTypes,
  backgroundSize: PropTypes.oneOf(['contain', 'cover']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureColor: PropTypes.string,
  disclosureOnBackground: PropTypes.bool,
  size: PropTypes.string,
  textColor: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.element),
  fontChronicle: PropTypes.bool,
};

CenteredHero.defaultProps = {
  iconImage: '',
  preheader: '',
  title: 'Title goes here',
  subtitle:
    'Description (desktop) goes here. Mobile will default to this if no mobile subtitle provided.',
  backgroundSize: 'contain',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  size: '',
  ...BackgroundColor.defaultProps,
  fontChronicle: false,
};

CenteredHero.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  textColorPicker,
  stringInputSpec('iconImage', 'Icon Image URL'),
  preheaderInput,
  titleInput,
  boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
  subtitleInput,
  stringTextAreaSpec('subtitleMobile', 'Mobile subtitle'),
  stringSelectSpec(
    'backgroundSize',
    'Background Size',
    [
      { label: 'Contain', value: 'contain' },
      { label: 'Cover', value: 'cover' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringInputSpec(
    'backgroundImageSrcDesktop',
    'Background Image URL (Desktop)'
  ),
  stringInputSpec('backgroundImageSrcTablet', 'Background Image URL (Tablet)'),
  stringInputSpec('backgroundImageSrcMobile', 'Background Image URL (Mobile)'),
  ...Disclosure.getEditingConfig(),
];

CenteredHero.displayName = 'CenteredHero';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(CenteredHero);
